import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/hero-patio.webp";
import { CTAContactForm } from "./CTAContactForm";

interface CTASectionProps {
  serviceName?: string;
  heading?: string;
  highlightedHeading?: string;
  showFinancing?: boolean;
}

export function CTASection({ serviceName, heading = "Ready to", highlightedHeading = "Get Started?", showFinancing = false }: CTASectionProps) {
  return (
    <section id="contact" className="relative py-24">
      {/* Background image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-background/90 backdrop-blur-sm" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Left Column - Text Content */}
          <div className="text-center lg:text-left lg:sticky lg:top-24 lg:self-start">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {heading} <span className="text-gradient-gold">{highlightedHeading}</span>
            </h2>
            
            <p className="text-lg text-muted-foreground mb-8">
              If you've got a project you've been putting off, or something that needs 
              attention now, we're here to help. No pressure, just honest advice.
            </p>

            {showFinancing && (
              <p className="text-lg text-muted-foreground mb-8">
                Financing options are available.{" "}
                <Link to="/financing" className="text-secondary hover:underline">
                  See your monthly payment options
                </Link>
                .
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button variant="goldOutline" size="xl" asChild>
                <a href="tel:+19737252395" className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Call or Text (973) 725-2395
                </a>
              </Button>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="glass-card rounded-xl p-6 md:p-8">
            <h3 className="text-xl md:text-2xl font-bold mb-2 text-center">
              Need A Mason Contractor?{" "}
              <span className="text-gradient-gold">Request An Estimate!</span>
            </h3>
            <p className="text-muted-foreground mb-6 text-center text-sm">
              Fill out the form and we'll get back to you promptly.
            </p>
            <CTAContactForm serviceName={serviceName} />
          </div>
        </div>
      </div>
    </section>
  );
}
